import { useCallback, useMemo, useSyncExternalStore } from 'react';
import { assetsOrgAssetsCreate } from '@goldcast/api/content';
import { useClipAsset } from '../useClipAsset';
import { remotionPlayerStateStore } from '../../ClipNewTimeline/store/remotionPlayerState';
import { AddMenuItem } from '../../ClipNewTimeline/TimelinePlayerActionBar/AddMenu/types';
import { setIsAddingAudio, timelineActionsStore } from '../../ClipNewTimeline/store/timelineActions';
import { updateElementDetailsHandler } from '../handlers';
import { findTrackElementById } from '../utils';
import { ASSET_PATH, DEFAULT_AUDIO_DURATION, MUSIC_LIBRARY } from '../constants';
import { addToRecentlyUsed } from './audioOverlayLibraryUtils';
import { AudioElement } from '@/App/remotion/MainPlayer/types/Element/AudioElement';
import { useMultiAssetUploader } from '@/components/molecules/MultiAssetUploader/useMultiAssetUploader';
import { AUDIO_FORMATS, AUDIO_MAX_SIZE } from '@/components/molecules/MultiAssetUploader/constants';
import { currentUser } from '@/stores/user';
import { getRelativePlayerTime } from '@/App/remotion/MainPlayer/utils/deletesUtils';
import { useAppContext } from '@/context/AppContext/AppContext';
import { showErrorToast, showSuccessToast } from '@/libs/toast/toast';
import { trackClipUpdate } from '@/stores/clip';
import { useClipsContext } from '@/context/ClipsContext/ClipsContext';
import { ElementType } from '@/App/remotion/MainPlayer/types/Element/Type';
import EventBus from '@/libs/eventBus/eventBus';
import { CustomEvents } from '@/libs/eventBus/constants';
import { FeatureFlagKeys } from '@/services/featureFlag';
import featureFlagStore from '@/stores/featureFlagStore';

interface UseAudioOverlayReturn {
  uploadAudio: (files: File[]) => Promise<void>;
  acceptedFormats: string;

  selectLibraryMusic: (musicId: string) => Promise<void>;

  addAudioOverlayMenuItem: AddMenuItem;

  toggleAudioLoop: (elementId: string) => Promise<void>;
  isAudioElementLooping: (elementId: string) => boolean;
  getAudioElementDuration: (elementId: string) => number;
}

/**
 * Custom hook for audio overlay functionality
 * Provides unified upload logic for both popup and timeline flows
 */
export const useAudioOverlay = (): UseAudioOverlayReturn => {
  const { fps, clipId, clipData } = useClipsContext();

  const { addClipAsset } = useClipAsset();
  const { logger } = useAppContext();
  const { isAddingAudio } = useSyncExternalStore(timelineActionsStore.subscribe, timelineActionsStore.getSnapshot);
  const featureFlags = useSyncExternalStore(featureFlagStore.subscribe, featureFlagStore.getSnapshot);
  const isUseCLReplaceAudioEnabled = featureFlags[FeatureFlagKeys.Use_CL_Replace_Audio];

  const organization = currentUser.getSnapshot()?.organization;
  const baseFilePath = useMemo(() => `${organization}/clip-asset`, [organization]);

  const getCurrentFrame = useCallback((): number => {
    const currentTime = remotionPlayerStateStore.getSnapshot().currentTime;
    return getRelativePlayerTime({ currentTime, deleteBounds: [] }) * fps - 1;
  }, [fps]);

  const processUploadedAssets = useCallback(
    async (validFiles: any[], frame: number) => {
      try {
        await assetsOrgAssetsCreate({
          body: [
            {
              name: validFiles[0].filename,
              source_url: validFiles[0].url,
              type: 'AUDIO'
            }
          ],
          queryParams: {}
        });

        await addClipAsset({
          currentFrame: frame,
          asset: validFiles[0],
          type: 'audio'
        });

        trackClipUpdate(clipId, 'AUDIO_OVERLAY');
        setIsAddingAudio(false);
        showSuccessToast('Audio uploaded successfully');
      } catch (error) {
        logger.error(error);
        setIsAddingAudio(false);
        showErrorToast('Failed to upload audio');
        throw error;
      }
    },
    [addClipAsset, clipId, logger]
  );

  const { uploadFiles, acceptedFormats } = useMultiAssetUploader({
    baseFilePath,
    assetPath: ASSET_PATH,
    formats: AUDIO_FORMATS,
    maxSize: AUDIO_MAX_SIZE,
    handleUploadedAssets: async ({ validFiles }) => {
      if (validFiles.length > 0) {
        const frame = getCurrentFrame();
        await processUploadedAssets(validFiles, frame);
      }
    },
    onError: error => {
      logger.error(error);
      showErrorToast('message' in error ? error.message : 'Failed to upload audio');
    }
  });

  const uploadAudio = useCallback(
    async (files: File[]): Promise<void> => {
      if (!files.length) {
        return;
      }

      setIsAddingAudio(true);

      try {
        await uploadFiles(files);
      } catch (error) {
        logger.error(error);
        setIsAddingAudio(false);
        showErrorToast('Failed to upload audio');
      }
    },
    [uploadFiles, logger]
  );

  const selectLibraryMusic = useCallback(
    async (musicId: string): Promise<void> => {
      const musicTrack = MUSIC_LIBRARY[musicId];
      if (!musicTrack) {
        showErrorToast('Music track not found');
        return;
      }

      setIsAddingAudio(true);

      try {
        const frame = getCurrentFrame();

        await addClipAsset({
          currentFrame: frame,
          asset: {
            filename: musicTrack.filename,
            url: musicTrack.url,
            type: 'audio',
            name: musicTrack.name
          },
          type: 'audio'
        });

        addToRecentlyUsed(musicId);

        trackClipUpdate(clipId, 'AUDIO_OVERLAY');
        setIsAddingAudio(false);
        showSuccessToast('Music added successfully');
      } catch (error) {
        logger.error('Failed to add library music:', error);
        setIsAddingAudio(false);
        showErrorToast('Failed to add music from library');
      }
    },
    [getCurrentFrame, addClipAsset, clipId, logger]
  );

  const addAudioOverlayMenuItem: AddMenuItem = useMemo(
    () => ({
      id: 'add-audio-overlay',
      label: isAddingAudio ? 'Adding Audio...' : 'Add Audio',
      isDisabled: isAddingAudio,
      iconName: 'IconHeadphones',
      onClick: async () => {
        if (isUseCLReplaceAudioEnabled) {
          EventBus.dispatch(CustomEvents.OpenAudioOverlayLibraryModal);
          return;
        }

        return new Promise<void>((resolve, reject) => {
          const input = document.createElement('input');
          input.type = 'file';
          input.accept = acceptedFormats;
          input.multiple = false;

          input.onchange = async e => {
            try {
              const files = Array.from((e.target as HTMLInputElement).files || []);
              await uploadAudio(files);
              resolve();
            } catch (error) {
              reject(error);
            }
          };

          input.click();
        });
      }
    }),
    [isAddingAudio, isUseCLReplaceAudioEnabled, acceptedFormats, uploadAudio]
  );

  const calculateEffectiveAudioDuration = useCallback(
    (element: AudioElement, timelineDuration: number): number => {
      const originalDuration = element.details?.duration || DEFAULT_AUDIO_DURATION;
      const isLooping = Boolean(element.details?.loop);

      if (!isLooping) {
        return originalDuration;
      }

      const remainingTime = timelineDuration - element.fromFrame / fps;
      return Math.max(remainingTime, originalDuration);
    },
    [fps]
  );

  const toggleAudioLoop = useCallback(
    async (elementId: string): Promise<void> => {
      if (!clipData) {
        showErrorToast('Clip data not available');
        return;
      }

      const element = findTrackElementById(clipData, elementId, ElementType.AUDIO) as AudioElement | null;
      if (!element) {
        showErrorToast('Audio element not found');
        return;
      }

      try {
        const newLoopState = !element.details?.loop;
        const sourceDurationSec = element.details?.duration ?? DEFAULT_AUDIO_DURATION;
        const currentDurationSec = (element.toFrame - element.fromFrame) / fps;
        const shouldClampDuration = !newLoopState && currentDurationSec > sourceDurationSec && sourceDurationSec > 0;
        const newToFrame = shouldClampDuration ? element.fromFrame + Math.round(sourceDurationSec * fps) : undefined;

        updateElementDetailsHandler(
          clipData,
          clipId,
          elementId,
          { loop: newLoopState },
          newToFrame !== undefined ? { toFrame: newToFrame } : undefined
        );
      } catch (error) {
        logger.error('Failed to toggle audio loop:', error);
        showErrorToast('Failed to toggle audio loop');
      }
    },
    [clipData, clipId, fps, logger]
  );

  const isAudioElementLooping = useCallback(
    (elementId: string): boolean => {
      const element = findTrackElementById(clipData, elementId, ElementType.AUDIO) as AudioElement | null;
      return Boolean(element?.details?.loop);
    },
    [clipData]
  );

  const getAudioElementDuration = useCallback(
    (elementId: string): number => {
      if (!clipData) {
        return 0;
      }

      const element = findTrackElementById(clipData, elementId, ElementType.AUDIO) as AudioElement | null;
      if (!element) {
        return 0;
      }

      return calculateEffectiveAudioDuration(element, clipData.asset_metadata.duration || 0);
    },
    [clipData, calculateEffectiveAudioDuration]
  );

  return {
    uploadAudio,
    selectLibraryMusic,
    acceptedFormats,
    addAudioOverlayMenuItem,
    toggleAudioLoop,
    isAudioElementLooping,
    getAudioElementDuration
  };
};
